/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Sunday, May 8th 2022, 10:30:10 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import { useNavigate } from "react-router-dom";

import { resolveURLName } from "core/routes/utils";
import { OSActionButton, useToggleState, PlainDialog, CircularProgressCentered } from "ui-widgets";
import { useWorkflowContext } from "service-builder/providers";
import { ActionDialog } from "ui-widgets/dialogs/GenericDialogs";
import { getDefaultTaskTemplate } from "lib/factories";
import { TaskTemplate } from "lib/schemas";
import { useTaskTemplateAPIForm } from "service-builder/services";
import { useServiceBuilderContext } from "service-builder/providers";

export const NewDocumentTaskTemplateForm = ({ open, handleClose }: { open: boolean; handleClose: () => void }) => {
  const {
    useTaskTemplateAPIFormReturn,
    successDialogOpenState,
    navigateToDocumentTemplateFn,
    navigateToWorkflowBuilderFn,
    isCreatingDocumentTemplate,
  } = useNewDocumentTaskTemplateFormLogic();

  return (
    <>
      {isCreatingDocumentTemplate ? (
        <LoadingDialog />
      ) : (
        <>
          <SuccessDialog
            successDialogOpenState={successDialogOpenState}
            navigateToDocumentTemplateFn={navigateToDocumentTemplateFn}
            navigateToWorkflowBuilderFn={navigateToWorkflowBuilderFn}
          />

          <ConfirmCreateDocumentTemplate
            closeDialogFn={handleClose}
            dialogOpenState={open}
            createDocumentTaskTemplateFn={useTaskTemplateAPIFormReturn.submitAPIForm}
          />
        </>
      )}
    </>
  );
};

const useNewDocumentTaskTemplateFormLogic = () => {
  const {
    taskTemplateListContext: { updateList, endOfListPosition, selectItem },
  } = useWorkflowContext();

  const {
    processDefinitionContext: { currentProcessDefinition },
  } = useServiceBuilderContext();

  const newDocumentTemplateDefaultValues = getDefaultTaskTemplate({
    processDefinition: currentProcessDefinition,
    bpmnNodeType: "Handler",
    defaultSequence: endOfListPosition,
    title: "Generate Document",
    optionalProperties: { automated_task_type: "generate_document" },
  });

  const { open: successDialogOpenState, handleOpen: openSuccessDialogFn } = useToggleState();

  const [createdDocumentTaskTemplate, setCreatedDocumentTaskTemplate] = React.useState<TaskTemplate>();

  const onSaveDocumentTaskTemplate = (createdDocumentTaskTemplate: TaskTemplate) => {
    updateList(createdDocumentTaskTemplate);
    setCreatedDocumentTaskTemplate(createdDocumentTaskTemplate);
    openSuccessDialogFn();
  };

  const navigate = useNavigate();

  const navigateToWorkflowBuilderFn = () => {
    if (!createdDocumentTaskTemplate?.id) return;

    selectItem(createdDocumentTaskTemplate.id);
    navigate(resolveURLName("serviceBuilderRoot", { serviceId: currentProcessDefinition.id }));
  };

  const navigateToDocumentTemplateFn = () => {
    if (!createdDocumentTaskTemplate?.document) return;

    navigate(
      resolveURLName("documentTemplate", {
        serviceId: currentProcessDefinition.id,
        documentTemplateId: createdDocumentTaskTemplate.document,
      })
    );
  };

  const useTaskTemplateAPIFormReturn = useTaskTemplateAPIForm(
    newDocumentTemplateDefaultValues?.id,
    newDocumentTemplateDefaultValues,
    onSaveDocumentTaskTemplate
  );

  return {
    useTaskTemplateAPIFormReturn,
    successDialogOpenState,
    navigateToWorkflowBuilderFn,
    navigateToDocumentTemplateFn,
    isCreatingDocumentTemplate: useTaskTemplateAPIFormReturn.isSendingDataToAPI,
  };
};

export const ConfirmCreateDocumentTemplate = ({ createDocumentTaskTemplateFn, closeDialogFn, dialogOpenState }) => {
  return (
    <ActionDialog
      severity="info"
      title={"Create Automated Document Generation Task"}
      open={dialogOpenState}
      onClose={closeDialogFn}
      confirmationAction={() => {
        createDocumentTaskTemplateFn();
        closeDialogFn();
      }}
      dialogMessage={
        <p>
          The document task template to be created will automatically generate documents at the specified point in your
          worflow. Confirm to create.
        </p>
      }
    />
  );
};

interface SuccessDialogProps {
  successDialogOpenState: boolean;
  navigateToWorkflowBuilderFn: () => void;
  navigateToDocumentTemplateFn: () => void;
}

const SuccessDialog = ({
  successDialogOpenState,
  navigateToWorkflowBuilderFn,
  navigateToDocumentTemplateFn,
}: SuccessDialogProps) => {
  return (
    <ActionDialog
      severity="success"
      title="Successfully created new document generation task template"
      open={successDialogOpenState}
      onClose={() => {}} // Dont close on clickaway so that the dialog can remain and user must take action to avoid them accidentally creating multiple doc task templates
      renderActions={() => (
        <>
          <OSActionButton size="large" variant="contained" onClick={navigateToWorkflowBuilderFn}>
            Open in Workflow Builder
          </OSActionButton>
          <OSActionButton size="large" variant="contained" onClick={navigateToDocumentTemplateFn}>
            Design Document Template
          </OSActionButton>
        </>
      )}
      dialogMessage={
        <p>
          You have successfully created a new document generation task template. The task has been appended to the end
          of the workflow. You can go and adjust this in the workflow builder.
        </p>
      }
    />
  );
};

export const LoadingDialog = () => (
  <PlainDialog open={true} title="Creating Document Generation Task Template">
    <CircularProgressCentered />
  </PlainDialog>
);
